import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 75, 100];
export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage?: number;
  totalItems?: number;
  /**
   * When provided, a "Rows per page" selector is shown alongside the page
   * controls. Leave undefined to keep the original fixed-page-size behaviour.
   */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  itemLabel?: string;
  className?: string;
  'data-id'?: string;
}
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  totalItems,
  onItemsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  itemLabel,
  className = '',
  'data-id': dataId
}: PaginationProps) {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  const showRowsPerPage = typeof onItemsPerPageChange === 'function' && typeof itemsPerPage === 'number';
  // Generate page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      if (currentPage > 3) {
        pages.push('ellipsis-start');
      }
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }
      // Always show last page
      pages.push(totalPages);
    }
    return pages;
  };
  const pageNumbers = getPageNumbers();
  // Calculate page count text
  const getPageCountText = () => {
    if (!itemsPerPage || !totalItems) return null;
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    return `Showing ${start}-${end} of ${totalItems}${itemLabel ? ` ${itemLabel}` : ''}`;
  };
  return <div className={`flex flex-wrap items-center justify-between gap-4 ${className}`} data-id={dataId}>
      {/* Page count text */}
      {getPageCountText() && <div className="text-[13px] text-[#64748B]" data-id="element-5629" data-mp="21xapmbxwykk2">{getPageCountText()}</div>}

      <div className="flex items-center gap-4 ml-auto" data-id="element-5630" data-mp="21zdq5pwozw31">
        {/* Rows per page */}
        {showRowsPerPage && <div className="flex items-center gap-2" data-id="element-5631" data-mp="1n6jvuycrhbeb">
            <label htmlFor="rows-per-page" className="text-[13px] text-[#64748B] whitespace-nowrap" data-id="element-5632" data-mp="1s8j0sco5ggdd">
              Rows per page
            </label>
            <select id="rows-per-page" value={itemsPerPage} onChange={event => onItemsPerPageChange?.(Number(event.target.value))} className="h-9 pl-3 pr-8 text-[13px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md cursor-pointer hover:bg-[#F1F5F9] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" aria-label="Rows per page" data-id="element-5633" data-mp="1ea2lvyg4vjk5">
              {rowsPerPageOptions.map(option => <option key={option} value={option} data-id="element-5634" data-mp="14j1b6awgbxiq">
                  {option}
                </option>)}
            </select>
          </div>}

        {/* Pagination controls */}
        <nav className="flex items-center gap-1" aria-label="Pagination" data-id="element-5635" data-mp="1dl24iwokdzf4">
          {/* Previous button */}
          <button onClick={() => canGoPrevious && onPageChange(currentPage - 1)} disabled={!canGoPrevious} className="flex items-center justify-center w-9 h-9 text-[13px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F1F5F9] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" aria-label="Go to previous page" data-id="element-5636" data-mp="1sa6p2kiu0fqe">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-5637" data-mp="3qr8mbzj6vrg8" />
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-1" role="list" data-id="element-5638" data-mp="3voh3bwslppfw">
            {pageNumbers.map((page, index) => {
            if (typeof page === 'string') {
              return <span key={`${page}-${index}`} className="w-9 h-9 flex items-center justify-center text-[13px] font-medium text-[#94A3B8]" aria-hidden="true" data-id="element-5639" data-mp="8cg6wttw9ypd">
                    ...
                  </span>;
            }
            return <button key={page} onClick={() => onPageChange(page)} className={`
                    w-9 h-9 text-[13px] font-medium rounded-md transition-colors duration-200
                    focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
                    ${page === currentPage ? 'bg-[#0EA5E9] text-white hover:bg-[#0284C7]' : 'bg-white border border-[#CBD5E1] text-[#475569] hover:bg-[#F1F5F9]'}
                  `} aria-label={`Go to page ${page}`} aria-current={page === currentPage ? 'page' : undefined} data-id="element-5640" data-mp="3crzepo91u5nv" data-mpi={index}>
                  {page}
                </button>;
          })}
          </div>

          {/* Next button */}
          <button onClick={() => canGoNext && onPageChange(currentPage + 1)} disabled={!canGoNext} className="flex items-center justify-center w-9 h-9 text-[13px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F1F5F9] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" aria-label="Go to next page" data-id="element-5641" data-mp="11fikcs3omosc">
            <ChevronRightIcon className="h-4 w-4" data-id="element-5642" data-mp="ihpt4n2f83as" />
          </button>
        </nav>
      </div>
    </div>;
}